import { Calendar, FileText, CheckSquare, MapPin, DollarSign, Users, StickyNote, type LucideIcon } from "lucide-react";

// Extraction type identifiers
export type ExtractionType = "dates" | "summary" | "checklist" | "location" | "budget" | "client" | "notes";

// --- API Response Types ---

export interface DateCard {
  date: string;
  description: string;
  search_text: string;
}

export interface ExtractDatesResponse {
  date_cards: DateCard[];
}

export interface ExtractSummaryResponse {
  summary: string;
}

export interface ExtractedChecklistItem {
  description: string;
  search_text: string;
}

export interface ExtractedChecklist {
  title: string;
  items: ExtractedChecklistItem[];
}

export interface ExtractChecklistResponse {
  checklists: ExtractedChecklist[];
}

export interface ExtractedLocation {
  query: string;
  address: string;
  place_name: string;
  city: string;
  region: string;
  country: string;
  search_text: string;
}

export interface ExtractLocationResponse {
  locations: ExtractedLocation[];
}

export interface ExtractedBudget {
  search_text: string;
  [key: string]: string;
}

export interface ExtractBudgetResponse {
  budgets: ExtractedBudget[];
}

export interface ExtractedClient {
  name: string;
  website: string;
  address: string;
  search_text: string;
}

export interface ExtractClientResponse {
  clients: ExtractedClient[];
}

export interface ExtractedNote {
  name: string;
  content: string;
}

export interface ExtractNotesResponse {
  notes: ExtractedNote[];
}

// Union type for all extraction results
export type ExtractionResults =
  | ExtractDatesResponse
  | ExtractSummaryResponse
  | ExtractChecklistResponse
  | ExtractLocationResponse
  | ExtractBudgetResponse
  | ExtractClientResponse
  | ExtractNotesResponse;

// --- UI State Types ---

// Extraction operation metadata for the operation list UI
export interface ExtractionOperation {
  type: ExtractionType;
  label: string;
  description: string;
  icon: LucideIcon;
}

export const EXTRACTION_OPERATIONS: ExtractionOperation[] = [
  { type: "dates", label: "Extract Dates", description: "Find date mentions and deadlines", icon: Calendar },
  { type: "summary", label: "Extract Summary", description: "Generate a document summary", icon: FileText },
  { type: "checklist", label: "Extract Checklist", description: "Find action items and tasks", icon: CheckSquare },
  { type: "location", label: "Extract Location", description: "Find addresses and places", icon: MapPin },
  { type: "budget", label: "Extract Budget", description: "Find financial information", icon: DollarSign },
  { type: "client", label: "Extract Client", description: "Find clients and organizations", icon: Users },
  { type: "notes", label: "Extract Notes", description: "Extract structured notes", icon: StickyNote },
];
